import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Navigation, MapPin, Camera, Play, Square, Loader2, X, ChevronDown, ChevronUp } from 'lucide-react';
import LiveTrackingMap from './LiveTrackingMap';
import PinMap from './PinMap';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : null);

const ServiceTracking = ({ booking, effectiveRole, onTrackingUpdate, showLocationMap = false, collapsible = false, address = null }) => {
  const bookingId = booking.id;
  const isProvider = effectiveRole === 'provider';
  const [t, setT] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showBefore, setShowBefore] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');
  const [open, setOpen] = useState(!collapsible);

  const fetchTracking = useCallback(async () => {
    try { const r = await axios.get(`${API}/bookings/${bookingId}/tracking`); setT(r.data); } catch (_) {}
  }, [bookingId]);

  useEffect(() => { fetchTracking(); }, [fetchTracking]);
  useEffect(() => { if (onTrackingUpdate) onTrackingUpdate(t); }, [t, onTrackingUpdate]);

  useEffect(() => {
    if (!t || !t.on_my_way_at || t.job_started_at || t.job_ended_at) return;
    const iv = setInterval(fetchTracking, 15000);
    return () => clearInterval(iv);
  }, [t, fetchTracking]);

  const act = async (path, body) => {
    setBusy(true); setErr('');
    try { await axios.post(`${API}/bookings/${bookingId}/${path}`, body || {}); await fetchTracking(); return true; }
    catch (e) { setErr(e.response?.data?.detail || 'Action failed.'); return false; }
    finally { setBusy(false); }
  };

  // Tap "On My Way": grab the provider's current position (so the client gets an
  // ETA push immediately), then start the trip. Falls back to no-location if denied.
  const startOnMyWay = () => {
    if (!navigator.geolocation) { act('on-my-way'); return; }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { act('on-my-way', { lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      () => { act('on-my-way'); },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  };

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 5 - photos.length);
    if (!files.length) return;
    setUploading(true); setErr('');
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        const r = await axios.post(`${API}/upload/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        urls.push(r.data.url);
      }
      setPhotos((p) => [...p, ...urls].slice(0, 5));
    } catch (_) { setErr('Photo upload failed.'); }
    finally { setUploading(false); }
  };

  const proceedStart = async () => {
    if (photos.length) await axios.post(`${API}/bookings/${bookingId}/before-photos`, { photos }).catch(() => {});
    const ok = await act('start-job');
    if (ok) setShowBefore(false);
  };

  if (!t) return null;

  const hasPin = booking.service_lat != null && booking.service_lng != null;
  const trackingRelevant = ['accepted', 'in_progress'].includes(t.status) && !t.job_ended_at;
  const sectionVisible = trackingRelevant || (showLocationMap && hasPin);
  if (!sectionVisible) return null;

  const started = !!t.job_started_at;
  const onWay = !!t.on_my_way_at;
  const arrived = !!t.arrived_at;
  const showLiveMap = onWay && !started;

  const statusLabel = t.job_ended_at ? 'Completed'
    : started ? `In progress · ${fmtTime(t.job_started_at)}`
    : arrived ? `Arrived · ${fmtTime(t.arrived_at)}`
    : onWay ? `On the way · ${fmtTime(t.on_my_way_at)}`
    : (trackingRelevant ? 'Not started yet' : 'Tap to view');

  return (
    <div className="bg-sky-50 rounded-xl p-4 border border-sky-100" data-testid="service-tracking">
      <button
        type="button"
        onClick={() => collapsible && setOpen((o) => !o)}
        className={`w-full flex items-center justify-between gap-2 ${collapsible ? 'cursor-pointer' : 'cursor-default'}`}
        data-testid="service-tracking-toggle"
      >
        <span className="flex items-center gap-2">
          <Navigation className="w-4 h-4 text-sky-700" />
          <span className="font-semibold text-sky-800">Service Location &amp; Tracking</span>
        </span>
        <span className="flex items-center gap-2">
          <span className="text-xs text-sky-700 font-medium">{statusLabel}</span>
          {collapsible && (open ? <ChevronUp className="w-4 h-4 text-sky-700" /> : <ChevronDown className="w-4 h-4 text-sky-700" />)}
        </span>
      </button>

      {open && (
        <div className="mt-3">
          {address && (
            <p className="text-sm text-gray-700 mb-3 flex items-start gap-1" data-testid="tracking-address">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-green-600" />
              <span>{address}</span>
            </p>
          )}
          {err && <div className="mb-3 text-sm bg-red-100 text-red-700 rounded-lg px-3 py-2" data-testid="tracking-err">{err}</div>}

          {/* Tracking timeline (only for active bookings) */}
          {trackingRelevant && (
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600 mb-3">
              <span className={onWay ? 'text-sky-700 font-medium' : ''}>{onWay ? `On the way · ${fmtTime(t.on_my_way_at)}` : 'Not started'}</span>
              {arrived && <span className="text-sky-700 font-medium">Arrived · {fmtTime(t.arrived_at)}</span>}
              {started && <span className="text-green-700 font-medium">Job started · {fmtTime(t.job_started_at)}</span>}
              {t.service_duration_minutes != null && <span className="text-gray-700">Duration: {t.service_duration_minutes} min</span>}
            </div>
          )}

          {/* Single consolidated map: live route when on the way, static pin otherwise */}
          {showLiveMap ? (
            <div className="mb-3">
              <LiveTrackingMap booking={booking} effectiveRole={effectiveRole} height="16rem" shareLocation={isProvider} testId="service-tracking-map" />
            </div>
          ) : (hasPin ? (
            <div className="mb-3">
              <PinMap lat={booking.service_lat} lng={booking.service_lng} height="15rem" testId="service-location-map" />
            </div>
          ) : null)}

          {/* Provider actions */}
          {isProvider && trackingRelevant && (
            <div className="space-y-2">
              {!onWay && (
                <button onClick={startOnMyWay} disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-sky-600 text-white rounded-lg text-sm font-semibold hover:bg-sky-700 disabled:opacity-50"
                  data-testid="on-my-way-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />} On My Way
                </button>
              )}
              {onWay && !arrived && !started && (
                <button onClick={() => act('arrived')} disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-white border border-sky-300 text-sky-700 rounded-lg text-sm font-semibold hover:bg-sky-100 disabled:opacity-50"
                  data-testid="arrived-btn">
                  <MapPin className="w-4 h-4" /> I've Arrived
                </button>
              )}
              {onWay && !started && !showBefore && (
                <button onClick={() => setShowBefore(true)} disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                  data-testid="start-job-open-btn">
                  <Play className="w-4 h-4" /> Start Job
                </button>
              )}

              {showBefore && !started && (
                <div className="bg-white rounded-lg border p-3" data-testid="before-photos-step">
                  <p className="text-sm font-medium text-gray-800 mb-1">Take "before" photos (up to 5)</p>
                  <p className="text-xs text-gray-500 mb-2">Record the condition before you start. Optional but recommended.</p>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {photos.map((u, i) => (
                      <div key={i} className="relative">
                        <img src={u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`} alt="before" className="w-16 h-16 object-cover rounded-lg border" />
                        <button onClick={() => setPhotos((p) => p.filter((_, idx) => idx !== i))} className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5" data-testid={`remove-before-${i}`}><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    {photos.length < 5 && (
                      <label className="w-16 h-16 flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50" data-testid="add-before-photo">
                        {uploading ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : <Camera className="w-5 h-5 text-gray-400" />}
                        <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={handlePhotoSelect} />
                      </label>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setShowBefore(false); }} className="flex-1 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200" data-testid="cancel-start-btn">Cancel</button>
                    <button onClick={proceedStart} disabled={busy || uploading} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 disabled:opacity-50" data-testid="proceed-start-btn">
                      {busy ? 'Starting…' : 'Proceed & Start'}
                    </button>
                  </div>
                </div>
              )}

              {started && !t.job_ended_at && (
                <button onClick={() => act('end-job')} disabled={busy}
                  className="w-full inline-flex items-center justify-center gap-2 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-black disabled:opacity-50"
                  data-testid="end-job-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4" />} End Job
                </button>
              )}
              {t.before_photos?.length > 0 && (
                <p className="text-xs text-gray-500" data-testid="before-count">{t.before_photos.length} before photo(s) saved.</p>
              )}
            </div>
          )}

          {/* Client status hints */}
          {!isProvider && trackingRelevant && (
            <div>
              {!onWay && <p className="text-sm text-gray-600" data-testid="client-tracking-wait">You'll see a live map here once your provider is on the way.</p>}
              {started && <p className="text-sm text-green-700 font-medium" data-testid="job-in-progress">Your provider is working on the job now.</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ServiceTracking;
